import type { MutableRefObject } from "react";
import React, { useMemo, useState } from "react";
import {
  Box,
  FormLayout,
  LegacyStack,
  TextField,
  Text,
  Checkbox,
  Label,
  IndexTable,
  InlineGrid,
  Select,
  BlockStack,
} from "@shopify/polaris";
import { updateHorseInventoryLevels, type HorseInventoryLevel } from "../../api_utils/requests";
import { assertNumber, assertString, extractMessageFromError, isEmpty, softAssertNumber } from "../../helper_functions/utils";
import type { EditableInventoryName, Option } from "../../api_utils/types";
import { EDITABLE_INVENTORY_NAMES } from "../../api_utils/types";

const defaultReason = "correction";
const defaultComment = "";

const options = [
  { label: "Correction (default)", value: "correction", key: "correction" },
  { label: "Count", value: "count_inventory", key: "count_inventory" },
  { label: "Received", value: "received", key: "received" },
  { label: "Return restock", value: "return_restock", key: "return_restock" },
  { label: "Damaged", value: "damaged", key: "damaged" },
  { label: "Theft or loss", value: "theft_or_loss", key: "theft_or_loss" },
  { label: "Promotion or donation", value: "promotion_or_donation", key: "promotion_or_donation" },
  { label: "Other", value: "other", key: "other" },
];

// Every name is shown; only EDITABLE_INVENTORY_NAMES take input.
// committed and incoming are managed (orders; POs/TOs), on_hand is generated.
const columns: { name: string; title: string; tooltip: string }[] = [
  { name: "on_hand", title: "On hand", tooltip: "The number of units physically at the location" },
  { name: "available", title: "Available", tooltip: "The number of units available for sale" },
  { name: "incoming", title: "Incoming", tooltip: "The number of units in purchase and transfer orders for the location" },
  { name: "committed", title: "Committed", tooltip: "The number of units set aside for sales orders" },
  { name: "reserved", title: "Reserved", tooltip: "The number of units set aside for draft orders" },
  { name: "damaged", title: "Damaged", tooltip: "The number of units damaged" },
  { name: "safety_stock", title: "Safety stock", tooltip: "The number of units set aside for safety stock" },
  { name: "quality_control", title: "Quality control", tooltip: "The number of units set aside for quality control" },
];

function isEditable(name: string): name is EditableInventoryName {
  return (EDITABLE_INVENTORY_NAMES as readonly string[]).includes(name);
}

type ChangedQuantities = Record<string, Partial<Record<EditableInventoryName, string>>>;

export function QuantityChangeForm({
  horseInventoryLevels,
  horseVariantId,
  disabled,
  tracked,
  saveCallback,
  discardCallback,
  setTotalAvailableQuantity,
  setErrorMessage,
  setIsDirty
}: {
  readonly horseInventoryLevels: HorseInventoryLevel[];
  readonly horseVariantId: number;
  readonly disabled: boolean;
  readonly tracked: boolean;
  readonly saveCallback: MutableRefObject<() => Promise<HorseInventoryLevel[]>>;
  readonly discardCallback: MutableRefObject<() => void>;
  readonly setTotalAvailableQuantity?: (totalAvailableQuantity: string) => void;
  readonly setErrorMessage: (message: string) => void;
  readonly setIsDirty: (isDirty: boolean) => void;
}): React.JSX.Element {
  const determineIsDirty = (newChangedQuantities: ChangedQuantities, newTrackedQuantity: boolean, newSelectedReason: string, newStockUpdateComment: string): boolean => {
    const anyQuantityChanged = Object.values(newChangedQuantities).some((quantities) => !isEmpty(quantities));
    const isDirty = anyQuantityChanged || newTrackedQuantity !== tracked || newSelectedReason !== defaultReason || newStockUpdateComment !== defaultComment;
    setIsDirty(isDirty);
    return isDirty;
  }

  const [trackedQuantity, setTrackedQuantity] = useState(tracked);
  const handleSetTrackedQuantity = (newTrackedQuantity: boolean): void => {
    setTrackedQuantity(newTrackedQuantity);
    determineIsDirty(
      changedQuantities,
      newTrackedQuantity,
      selectedReason,
      stockUpdateComment
    )
  };
  const [selectedReason, setSelectedReason] = useState(defaultReason);
  const handleSetSelectedReason = (newSelectedReason: string): void => {
    setSelectedReason(newSelectedReason);
    determineIsDirty(
      changedQuantities,
      trackedQuantity,
      newSelectedReason,
      stockUpdateComment
    )
  };
  const [stockUpdateComment, setStockUpdateComment] = useState(defaultComment);
  const handleSetStockUpdateComment = (newStockUpdateComment: string): void => {
    setStockUpdateComment(newStockUpdateComment);
    determineIsDirty(
      changedQuantities,
      trackedQuantity,
      selectedReason,
      newStockUpdateComment
    )
  };
  const [changedQuantities, setChangedQuantities] = useState<ChangedQuantities>({});

  const horseInventoryLevelsIndex = useMemo<Record<string, HorseInventoryLevel>>(() => {
    return horseInventoryLevels.reduce((acc, hil) => {
      acc[hil.updateOrCreateId] = hil;
      return acc;
    }, {});
  }, [horseInventoryLevels]);

  const handleChange = (newQuantity: string, updateOrCreateId: string, inventoryName: EditableInventoryName): void => {
    const cleanQuantity = newQuantity?.replace(/[^\d.-]/g, "");

    const newChangedQuantities = { ...changedQuantities };
    const hilQuantities = { ...newChangedQuantities[updateOrCreateId] };
    if (cleanQuantity === horseInventoryLevelsIndex[updateOrCreateId]?.[inventoryName]?.toString()) {
      delete hilQuantities[inventoryName];
    } else {
      hilQuantities[inventoryName] = cleanQuantity;
    }
    if (isEmpty(hilQuantities)) {
      delete newChangedQuantities[updateOrCreateId];
    } else {
      newChangedQuantities[updateOrCreateId] = hilQuantities;
    }
    setChangedQuantities(newChangedQuantities);
    determineIsDirty(
      newChangedQuantities,
      trackedQuantity,
      selectedReason,
      stockUpdateComment
    );
  };

  const handleSave = async (): Promise<HorseInventoryLevel[]> => {
    // An untracked variant holds no quantities, so don't send the ones still in the form
    const horse_inventory_levels = trackedQuantity ? horseInventoryLevels.filter((hil) => {
      return changedQuantities[hil.updateOrCreateId] !== undefined;
    }).map((hil) => {
      const attributes = {
        id: hil.id,
        horse_location_id: hil.horse_location_id,
        horse_variant_id: hil.horse_variant_id,
      };
      Object.entries(changedQuantities[hil.updateOrCreateId]).forEach(([inventoryName, quantity]) => {
        // If it already exists, allow it to be changed to 0
        // but if it doesn't exist, then empty still means untracked
        attributes[inventoryName] = hil.id ? assertNumber(quantity) : softAssertNumber(quantity);
      });
      return attributes;
    }) : [];

    const payload = {
      horse_variant_id: horseVariantId,
      horse_inventory_levels,
      tracked: trackedQuantity,
      reason: selectedReason,
      comment: stockUpdateComment,
    };

    try{
      const updatedHorseInventoryLevels = await updateHorseInventoryLevels(payload)
      setChangedQuantities({});
      setStockUpdateComment(defaultComment);
      setSelectedReason(defaultReason);
      setIsDirty(false);
      if (setTotalAvailableQuantity && trackedQuantity) {
        setTotalAvailableQuantity(updatedHorseInventoryLevels.reduce((accum, hil) => accum + hil.available, 0).toString());
      } else if (setTotalAvailableQuantity) {
        setTotalAvailableQuantity("∞");
      }

      return updatedHorseInventoryLevels;
    } catch (err: unknown) {
      Rollbar.error(err, payload);
      const errorMessage = extractMessageFromError(err);
      setErrorMessage(errorMessage);
      throw err;
    }
  };
  saveCallback.current = handleSave;

  const handleDiscard = (): void => {
    setChangedQuantities({});
    setTrackedQuantity(tracked);
    setSelectedReason(defaultReason);
    setStockUpdateComment(defaultComment);
    determineIsDirty(
      {},
      tracked,
      defaultReason,
      defaultComment
    );
  };
  discardCallback.current = handleDiscard;

  const quantityValue = (hil: HorseInventoryLevel, inventoryName: string): string => {
    if (!trackedQuantity) {
      return "";
    }
    if (inventoryName === "on_hand") {
      return onHandValue(hil);
    }
    const changed = changedQuantities[hil.updateOrCreateId]?.[inventoryName];
    if (changed !== undefined) {
      return changed;
    }
    return hil[inventoryName] !== undefined && hil[inventoryName] !== null ? assertString(hil[inventoryName]) : "";
  };

  // Mirrors the DB's generated column: available + COALESCE(each other name, 0).
  // No available means no on hand. Recomputed from the edited values so the
  // user sees where their edits land before saving.
  const onHandValue = (hil: HorseInventoryLevel): string => {
    const memberValue = (inventoryName: string): number | null => {
      const value = changedQuantities[hil.updateOrCreateId]?.[inventoryName] ?? hil[inventoryName];
      if (value === undefined || value === null || value === "") {
        return null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const available = memberValue("available");
    if (available === null) {
      return "";
    }
    const others = ["committed", "reserved", "damaged", "safety_stock", "quality_control"];
    return others.reduce((sum, name) => sum + (memberValue(name) ?? 0), available).toString();
  };

  const rows = horseInventoryLevels.map((hil, index) => {
    return (
      <IndexTable.Row
        id={`row-${hil.updateOrCreateId}`}
        key={`row-${hil.updateOrCreateId}`}
        position={index}
      >
        <IndexTable.Cell>
          <Label id={`${hil.horse_location_id}-label`} key={`${hil.horse_location_id}-label`}>
            {hil.horse_location.name}
          </Label>
        </IndexTable.Cell>
        {columns.map(({ name, title }) => (
          <IndexTable.Cell key={`field-${hil.updateOrCreateId}-${name}`}>
            {/* Wide enough for 6 digits; read-only cells are bare text and need less */}
            <Box minWidth={isEditable(name) ? "6rem" : "3rem"}>
              {isEditable(name) ? (
                <TextField
                  autoComplete="off"
                  // A HIL that does not exist yet is created through its available quantity
                  disabled={disabled || !trackedQuantity || (!hil.id && name !== "available")}
                  inputMode="numeric"
                  label={title}
                  labelHidden
                  onChange={(newQuantity: string): void => {
                    handleChange(newQuantity, hil.updateOrCreateId, name);
                  }}
                  selectTextOnFocus
                  type="integer"
                  value={quantityValue(hil, name)}
                />
              ) : (
                <Text alignment="end" as="span" tone="subdued">
                  {quantityValue(hil, name)}
                </Text>
              )}
            </Box>
          </IndexTable.Cell>
        ))}
      </IndexTable.Row>
    );
  });

  return (
    <FormLayout key={`horse-variants_hil-${horseVariantId}-quantity-change-form`}>
      {horseInventoryLevels.length > 0 && (
        <FormLayout.Group>
          <LegacyStack alignment="baseline" distribution="fillEvenly">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Checkbox checked={trackedQuantity} disabled={disabled} label="Track quantity" onChange={handleSetTrackedQuantity} />
            </div>
          </LegacyStack>
        </FormLayout.Group>
      )}
      {horseInventoryLevels.length === 0 ? (
        <Text as="p" variant="bodySm">
          No inventory levels found
        </Text>
      ) : (
        <BlockStack gap="200">
          <IndexTable
            headings={[
              { title: "Location" },
              ...columns.map((column) => ({ title: column.title, tooltipContent: column.tooltip })),
            ]}
            itemCount={horseInventoryLevels.length}
            selectable={false}
          >
            {rows}
          </IndexTable>
          <Select
            disabled={disabled}
            label="Reason"
            onChange={handleSetSelectedReason}
            options={options satisfies Option[]}
            value={selectedReason}
          />
          <InlineGrid>
            <Label id="stock-comment">Comment</Label>
            <TextField
              autoComplete="off"
              disabled={disabled}
              inputMode="text"
              label="Comment"
              labelHidden
              multiline={2}
              onChange={handleSetStockUpdateComment}
              selectTextOnFocus
              type="text"
              value={stockUpdateComment}
            />
          </InlineGrid>
        </BlockStack>
      )}
    </FormLayout>
  );
}
